import { useCallback, useEffect, useRef, useState } from 'react';

export type CameraStatus = 'idle' | 'requesting' | 'ready' | 'denied' | 'error';

// The MediaStreamTrack `torch` capability is a WICG spec extension that
// lib.dom hasn't picked up. Chrome/Edge on Android expose it; iOS Safari
// does not — feature-detect via getCapabilities().torch and render the
// toggle only when present.
type TorchCapabilities = MediaTrackCapabilities & { torch?: boolean };
type TorchConstraint = MediaTrackConstraintSet & { torch?: boolean };

export function useCamera() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Flipped by the unmount cleanup. If a `getUserMedia` promise resolves after
  // the component is gone, we'd otherwise assign a live MediaStream to a ref
  // that nothing will ever clean up — leaving the camera light on. This guard
  // catches that race (most visible under React StrictMode dev double-mounts).
  const unmountedRef = useRef(false);
  const [status, setStatus] = useState<CameraStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const start = useCallback(async () => {
    if (streamRef.current) return;
    setStatus('requesting');
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          // Ask for a high capture rate so the rVFC sampler (which fires
          // once per camera frame) has more frames to work with. `ideal`
          // degrades gracefully where the sensor/pipeline can't hit 60.
          frameRate: { ideal: 60 },
        },
        audio: false,
      });

      if (unmountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.playsInline = true;
        await videoRef.current.play().catch(() => undefined);
      }

      if (unmountedRef.current) {
        // Caught between the assignment and the play() — clean up now.
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
        return;
      }

      const track = stream.getVideoTracks()[0];
      const caps = track && typeof track.getCapabilities === 'function'
        ? (track.getCapabilities() as TorchCapabilities)
        : undefined;
      setTorchSupported(!!caps?.torch);
      setTorchOn(false);
      setStatus('ready');
    } catch (err: any) {
      if (unmountedRef.current) return;
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        setStatus('denied');
        setError('Camera permission denied.');
      } else {
        setStatus('error');
        setError(err?.message ?? String(err));
      }
    }
  }, []);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStatus('idle');
    setTorchSupported(false);
    setTorchOn(false);
  }, []);

  // Drive the torch to an explicit state. Used for the auto on-at-start /
  // off-at-stop behaviour where a toggle would race the current value.
  // No-ops (and clears support) when the device can't honor the constraint.
  const setTorch = useCallback(async (on: boolean) => {
    const stream = streamRef.current;
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: on } as TorchConstraint] });
      setTorchOn(on);
    } catch {
      // Some devices advertise the capability but reject the constraint under
      // certain lighting/battery states — drop it silently.
      setTorchSupported(false);
      setTorchOn(false);
    }
  }, []);

  const toggleTorch = useCallback(() => setTorch(!torchOn), [setTorch, torchOn]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      stop();
    };
  }, [stop]);

  return { videoRef, status, error, start, stop, torchSupported, torchOn, setTorch, toggleTorch };
}
